import React, { useEffect, useState, useRef } from 'react';
import { FileText, Download, Eye, EyeOff, PenLine, Check } from 'lucide-react';
import api from '../../api/client';

const statoInfo = (stato) => ({
  GENERATO: { color:'#888',    label:'Da leggere' },
  INVIATO:  { color:'#F59E0B', label:'Da leggere' },
  FIRMATO:  { color:'#22C55E', label:'Accettato'  },
}[stato] || { color:'#888', label:stato });

export default function ContrattiAllenatore() {
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [firmandoId, setFirmandoId] = useState(null);
  const urlRef = useRef(null);

  const load = () => {
    api.get('/dipendenti/me/contratti').then(r => setContratti(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }; }, []);

  const chiudiAnteprima = () => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPreviewId(null);
    setPreviewUrl(null);
  };

  const apriAnteprima = async (c) => {
    if (previewId === c.id) { chiudiAnteprima(); return; }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    setPreviewId(c.id);
    setPreviewUrl(null);
    try {
      const r = await api.get(`/dipendenti/me/contratti/${c.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      urlRef.current = url;
      setPreviewUrl(url);
    } catch(e) { alert('Errore anteprima: ' + (e.response?.data?.error || e.message)); setPreviewId(null); }
  };

  const scaricaContratto = async (id, fileName) => {
    try {
      const r = await api.get(`/dipendenti/me/contratti/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  const firmaContratto = async (id) => {
    if (!window.confirm(
      'Confermando dichiari di aver letto e accettato questo contratto.\n\n' +
      'Per perfezionare l\'accordo, dovrai comunque firmare di persona una copia e restituircela in originale.\n\n' +
      'Procedere?'
    )) return;
    setFirmandoId(id);
    try {
      await api.post(`/dipendenti/me/contratti/${id}/firma`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
    finally { setFirmandoId(null); }
  };

  return (
    <div style={{ maxWidth:'720px', margin:'0 auto' }}>
      <div style={{ marginBottom:'20px' }}>
        <h1 style={s.title}>I MIEI CONTRATTI</h1>
        <p style={s.sub}>Consulta, conferma la lettura e scarica i tuoi contratti.</p>
      </div>

      <div style={{ background:'#F4600C10', border:'1px solid #F4600C30', borderRadius:'10px', padding:'12px 16px', marginBottom:'20px', fontSize:'12px', color:'var(--text)', lineHeight:1.5 }}>
        Confermando la lettura dichiari di accettare i termini del contratto. Per perfezionare l'accordo,
        il contratto va comunque <strong>firmato di persona</strong> e restituito <strong>in originale</strong> alla società.
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>Caricamento...</div>
      ) : contratti.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#555', fontSize:'var(--font-size-sm)' }}>
          <FileText size={28} color="var(--text-dim)" style={{ marginBottom:'10px' }}/>
          <div>Nessun contratto disponibile al momento.</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {contratti.map(c => {
            const firmato = c.stato === 'FIRMATO';
            const si = statoInfo(c.stato || 'GENERATO');
            const inAnteprima = previewId === c.id;
            return (
              <div key={c.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', overflow:'hidden' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px' }}>
                  <FileText size={18} color="#555"/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'var(--font-size-sm)', fontWeight:'700' }}>{c.nome || c.template?.nome || 'Contratto'}</div>
                    <div style={{ fontSize:'var(--font-size-xs)', color:'#555' }}>
                      Anno {c.anno} · {new Date(c.created_at).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  <span style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'0.5px', color:si.color, background:`${si.color}15`, padding:'3px 9px', borderRadius:'6px', whiteSpace:'nowrap' }}>
                    {firmato && <Check size={10} style={{ verticalAlign:'-1px', marginRight:'3px' }}/>}
                    {si.label.toUpperCase()}
                  </span>
                </div>

                <div style={{ display:'flex', gap:'8px', padding:'0 16px 14px', flexWrap:'wrap' }}>
                  <button style={s.btnSec} onClick={() => apriAnteprima(c)}>
                    {inAnteprima ? <EyeOff size={13}/> : <Eye size={13}/>} {inAnteprima ? 'CHIUDI ANTEPRIMA' : 'ANTEPRIMA'}
                  </button>
                  <button style={s.btnSec} onClick={() => scaricaContratto(c.id, c.file_nome)}>
                    <Download size={13}/> SCARICA
                  </button>
                  {!firmato && (
                    <button style={s.btnPri} disabled={firmandoId === c.id} onClick={() => firmaContratto(c.id)}>
                      <PenLine size={13}/> {firmandoId === c.id ? 'INVIO...' : 'CONFERMO E ACCETTO'}
                    </button>
                  )}
                </div>

                {inAnteprima && (
                  <div style={{ borderTop:'1px solid var(--border,#1E1E1E)', background:'var(--bg-input)' }}>
                    {previewUrl ? (
                      <iframe src={previewUrl} title={`Anteprima ${c.nome || 'contratto'}`}
                        style={{ width:'100%', height:'70vh', border:'none', display:'block' }}/>
                    ) : (
                      <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>Caricamento anteprima...</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  title: { fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px', margin:0 },
  sub:   { fontSize:'var(--font-size-sm)', color:'#777', margin:'4px 0 0' },
  btnSec: { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', color:'var(--text)', borderRadius:'7px', padding:'7px 12px', fontSize:'11px', fontWeight:'700', cursor:'pointer' },
  btnPri: { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C20', border:'1px solid #F4600C40', color:'#F4600C', borderRadius:'7px', padding:'7px 12px', fontSize:'11px', fontWeight:'700', cursor:'pointer' },
};
